import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AddPreflightMarketInput, CurrencyCode, EnabledOpportunityTypes, IndustryDefaultBrandUpdate, MarketplaceOrderHandoffDefaults, NorthStarKpi, TenantProductLine } from "@nexus/contracts";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient, useInfiniteQuery, useQueries } from "@tanstack/react-query";
import { MAX_ACCESS_BY_TENANT_BATCH, MAX_USERS_ACCESS_BATCH } from "@nexus/contracts";
import { toast } from "sonner";
import { Sentry } from "@/lib/sentry";
import {
  allUsersQueryOptions,
  iamUsersInfiniteQueryOptions,
  userQueryOptions,
  userAccessQueryOptions,
  usersAccessChunkQueryOptions,
  accessGroupsByTenantsChunkQueryOptions,
  allTenantsQueryOptions,
  iamTenantsInfiniteQueryOptions,
  tenantQueryOptions,
  tenantPickerQueryOptions,
  accessByTenantQueryOptions,
  accessGroupsByTenantsQueryOptions,
  accessGroupQueryOptions,
  iamStatsQueryOptions,
  allIndustriesQueryOptions,
  invitesListQueryOptions,
  invitesInfiniteQueryOptions,
  identityHubKeys,
  iamAccessInfiniteQueryOptions,
  accessTemplatesQueryOptions,
  accessTemplateQueryOptions,
  opportunitySetKeys,
  invalidateIndustriesForTenant,
  adminTenantSettingsQueryOptions,
  personaOpportunitySetKeys,
  normalizedPersonaOpportunitySetTenantId,
  tenantOnboardingKeys,
  brandBrainKeys,
} from "@/lib/queries";
import { auditLogKeys } from "@/features/identity-hub/lib/queries/audit-log.queries";
import {
  grantUserAccess,
  revokeUserAccess,
  bulkGrantUserAccess,
  bulkRevokeUserAccess,
  revokeAllUserAccess,
  replaceUserAccess,
  registerUser,
  updateUser,
  deactivateUser,
  reactivateUser,
  provisionTenant,
  updateTenant,
  deactivateTenant,
  reactivateTenant,
  createAccessGroup,
  createAccessGroupsBulk,
  updateAccessGroup,
  deleteAccessGroup,
  restoreAccessGroup,
  updateAccessPermissions,
  createAccessTemplate,
  updateAccessTemplate,
  previewAccessTemplateCascade,
  changeAccessTemplateStatus,
  grantAccessTemplate,
  revokeAccessTemplate,
  applyTemplateToOrg,
  removeTemplateFromOrg,
  createInvite,
  createInvitesBulk,
  regenerateInvite,
  fetchInviteLink,
  resendInvite,
  revokeInvite,
  fetchAccessByTenant,
  adminUpdateTenantSettings,
  replaceAdminTenantPreflightMarketRefs,
  triggerTenantSync,
  editTenantOnboarding,
  initializeTenantOnboarding,
  TENANT_ONBOARDING_POLL_BASE_PATH,
  type StatusResult,
  INVITE_EMAILS_LIMIT_CODE,
  MAX_INVITE_EMAILS,
  type IamWindow,
  type UserAccessEntry,
  type AccessGroupItem,
  type AccessTemplateListFilters,
  type BulkInviteResultItem,
  type InviteListFilters,
  type IamUsersListFilters,
  type IamTenantsListFilters,
  type IamAccessListFilters,
} from "@/features/identity-hub/api/identity-hub.api";
import {
  AmberWorkerFailureError,
  resolveAmberErrorToastMessage,
  useAmberMutation,
} from "@/lib/use-amber-mutation";
import { resolveTemplateSaveErrorMessage } from "@/features/identity-hub/lib/template-save-error";
import { grantablePerTenantGroups, isPerTenantAssignable } from "@/features/identity-hub/lib/access-scope";
import { useGrantablePermissionTupleKeys } from "@/features/identity-hub/hooks/useGrantableCatalogKeys";
import { ApiError, getLocalUserFacingApiMessage, shouldSuppressLocalApiErrorToast } from "@/lib/api-client";
import { tenantSettingsKeys } from "@/lib/queries/tenant-settings.queries.js";
import { dashboardKeys } from "@/lib/queries/dashboard.queries.js";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import { refreshNexusToken } from "@/features/auth/lib/refresh";
import { mergeUsersAccess, rememberSettledUsersAccess, userAccessPhase, type UserAccessPhase } from "@/features/identity-hub/lib/merge-users-access";
import { patchAccessPermissionsInCache, patchAccessByTenantCaches } from "@/features/identity-hub/lib/patch-access-permissions-cache";

function stableChunk<T>(items: T[], size: number): T[][] {
  const sorted = [...items].sort();
  const out: T[][] = [];
  for (let i = 0; i < sorted.length; i += size) {
    out.push(sorted.slice(i, i + size));
  }
  return out;
}

function parseSessionProductLine(value: string | undefined): TenantProductLine | undefined {
  if (value === "CORE" || value === "COMP" || value === "CORE_AND_COMP") return value;
  return undefined;
}

// ─── Queries ────────────────────────────────────────────────────────

export function useAllUsers() {
  return useQuery(allUsersQueryOptions());
}

export function useIamUsers(filters: IamUsersListFilters, opts: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    ...iamUsersInfiniteQueryOptions(filters),
    enabled: opts.enabled ?? true,
  });
}

export function useUser(userId: string | null | undefined) {
  return useQuery(userQueryOptions(userId ?? ""));
}

export function useUserAccess(userId: string | null) {
  return useQuery(userAccessQueryOptions(userId ?? ""));
}

export function useAllTenants(opts: { includeArchived?: boolean } = {}) {
  return useQuery(allTenantsQueryOptions(opts));
}

export function useIamTenants(filters: IamTenantsListFilters, opts: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    ...iamTenantsInfiniteQueryOptions(filters),
    enabled: opts.enabled ?? true,
  });
}

export function useTenant(tenantId: string | null | undefined) {
  return useQuery(tenantQueryOptions(tenantId ?? ""));
}

/** Tenant dropdown options for IAM create/edit forms. */
export function useTenantPicker() {
  return useQuery(tenantPickerQueryOptions());
}

export function useIamAccess(filters: IamAccessListFilters, opts: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    ...iamAccessInfiniteQueryOptions(filters),
    enabled: opts.enabled ?? true,
  });
}

export function useAccessByTenant(tenantId: string, opts: { includeArchived?: boolean } = {}) {
  return useQuery(accessByTenantQueryOptions(tenantId, opts));
}

export function useAccessGroup(accessId: string) {
  return useQuery(accessGroupQueryOptions(accessId));
}

export function useIamStats(window: IamWindow, opts: { enabled?: boolean } = {}) {
  return useQuery({ ...iamStatsQueryOptions(window), enabled: opts.enabled ?? true });
}

export function useAllIndustries() {
  const isSuperTenant = useIsSuperTenant();
  return useQuery({ ...allIndustriesQueryOptions(), enabled: isSuperTenant });
}

export function useAdminTenantSettings(tenantId: string | null | undefined) {
  return useQuery(adminTenantSettingsQueryOptions(tenantId));
}

// ─── Derived Helpers ────────────────────────────────────────────────

export interface DerivedTenant {
  id: string;
  name: string;
  isActive: boolean;
  accessEntries: UserAccessEntry[];
}

export function deriveTenantsFromAccess(accessEntries: UserAccessEntry[] | undefined): DerivedTenant[] {
  if (!accessEntries) return [];
  const map = new Map<string, DerivedTenant>();
  for (const entry of accessEntries) {
    const tid = entry.access.tenantId;
    let tenant = map.get(tid);
    if (!tenant) {
      tenant = {
        id: tid,
        name: entry.access.tenant.name,
        isActive: true,
        accessEntries: [],
      };
      map.set(tid, tenant);
    }
    tenant.accessEntries.push(entry);
  }
  return Array.from(map.values());
}

// ─── Sidebar Sync Helper ────────────────────────────────────────────

/**
 * When the edited user is the currently logged-in user, refresh the Nexus session so
 * the dropdown tenant list picks up the access grant or revoke. `refreshNexusToken`
 * also writes the response's `accessIds` into `useAuth.user.acs` (and localStorage)
 * so client-side self-edit checks stay in sync. The backend resolves memberships
 * server-side, so requests already see the change; only the UI needs the refresh.
 */
export async function syncSidebarIfActiveUser(selectedUserEmail: string | undefined) {
  const auth = useAuth.getState();
  if (!auth.user || !selectedUserEmail) return;
  if (auth.user.email !== selectedUserEmail) return;

  try {
    await refreshNexusToken();
  } catch (err) {
    // Non-critical — sidebar picks up on next refresh. Leave a breadcrumb for tracing.
    Sentry.addBreadcrumb({
      category: "auth",
      message: "syncSidebarIfActiveUser: refreshNexusToken failed",
      level: "warning",
      data: { error: err instanceof Error ? err.message : String(err) },
    });
  }
}

// ─── Mutation Helpers ───────────────────────────────────────────────

const userInvalidateKeys = [identityHubKeys.users(), identityHubKeys.all, auditLogKeys.all] as const;

export function useRegisterUser() {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: registerUser,
    pollBasePath: "/users",
    successMessage: t("iam.toast.userRegistered"),
    invalidateKeys: userInvalidateKeys,
  });
}

export function useUpdateUser() {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: updateUser,
    pollBasePath: "/users",
    successMessage: t("iam.toast.userUpdated"),
    invalidateKeys: userInvalidateKeys,
  });
}

export function useDeactivateUser() {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: deactivateUser,
    pollBasePath: "/users",
    successMessage: t("iam.toast.userSuspended"),
    invalidateKeys: userInvalidateKeys,
  });
}

export function useReactivateUser() {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: reactivateUser,
    pollBasePath: "/users",
    successMessage: t("iam.toast.userReactivated"),
    invalidateKeys: userInvalidateKeys,
  });
}

export function useGrantAccess(opts?: { beforeToast?: () => Promise<void> }) {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: grantUserAccess,
    pollBasePath: "/users",
    successMessage: t("iam.toast.accessGranted"),
    invalidateKeys: userInvalidateKeys,
    beforeToast: opts?.beforeToast,
  });
}

export function useRevokeAccess(opts?: { beforeToast?: () => Promise<void> }) {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: revokeUserAccess,
    pollBasePath: "/users",
    successMessage: t("iam.toast.accessRevoked"),
    invalidateKeys: userInvalidateKeys,
    beforeToast: opts?.beforeToast,
  });
}

export function useBulkGrantAccess(opts?: { beforeToast?: () => Promise<void> }) {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: bulkGrantUserAccess,
    pollBasePath: "/users",
    successMessage: t("iam.toast.accessGranted"),
    invalidateKeys: userInvalidateKeys,
    beforeToast: opts?.beforeToast,
  });
}

export function useBulkRevokeAccess(opts?: { beforeToast?: () => Promise<void> }) {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: bulkRevokeUserAccess,
    pollBasePath: "/users",
    successMessage: t("iam.toast.accessRevoked"),
    invalidateKeys: userInvalidateKeys,
    beforeToast: opts?.beforeToast,
  });
}

export function useReplaceAccess(opts?: { beforeToast?: () => Promise<void> }) {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: replaceUserAccess,
    pollBasePath: "/users",
    successMessage: t("iam.toast.accessReplaced"),
    invalidateKeys: userInvalidateKeys,
    beforeToast: opts?.beforeToast,
  });
}

// ─── Tenant Mutations ───────────────────────────────────────────────

export function useProvisionTenant({ showSuccessToast = true }: { showSuccessToast?: boolean } = {}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useAmberMutation({
    mutationFn: provisionTenant,
    pollBasePath: "/tenants",
    successMessage: showSuccessToast ? t("iam.toast.organizationCreated") : undefined,
    invalidateKeys: [auditLogKeys.all],
    onSuccess: () => refreshIdentityHubInBackground(qc),
  });
}

export function useUpdateTenant() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useAmberMutation<{
    tenantId: string;
    changes: {
      name?: string;
      retoolUserId?: string | null;
      description?: string | null;
      brandColor?: string | null;
      type?: string;
      productLine?: string;
      industryIndices?: string[];
    };
  }>({
    mutationFn: updateTenant,
    pollBasePath: "/tenants",
    successMessage: t("iam.toast.organizationUpdated"),
    invalidateKeys: userInvalidateKeys,
    onSuccess: (_result, variables) => {
      // Sync sidebar tenant selector if the renamed tenant, or its product line, is in the session.
      const newName = variables.changes.name;
      const nextProductLine = parseSessionProductLine(variables.changes.productLine);
      if (newName || nextProductLine) {
        const auth = useAuth.getState();
        const match = auth.tenants.find((t) => t.id === variables.tenantId);
        if (match) {
          auth.updateTenants(
            auth.tenants.map((t) =>
              t.id === variables.tenantId
                ? {
                    ...t,
                    ...(newName ? { name: newName } : {}),
                    ...(nextProductLine ? { productLine: nextProductLine } : {}),
                  }
                : t,
            ),
          );
        }
      }
      if (variables.changes.industryIndices) {
        invalidateIndustriesForTenant(qc, variables.tenantId);
      }
    },
  });
}

export function useDeactivateTenant() {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: deactivateTenant,
    pollBasePath: "/tenants",
    successMessage: t("iam.toast.organizationArchived"),
    invalidateKeys: userInvalidateKeys,
  });
}

export function useReactivateTenant() {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: reactivateTenant,
    pollBasePath: "/tenants",
    successMessage: t("iam.toast.organizationRestored", "Organization restored"),
    invalidateKeys: userInvalidateKeys,
  });
}

const SHOWN_INLINE = () => true;

type OnboardingSavePhase = "verifying" | "saved" | null;

function useOnboardingSavePhase() {
  const [savePhase, setSavePhase] = useState<OnboardingSavePhase>(null);
  const onStatus = (status: StatusResult) => {
    if (status.status === "COMPLETED") setSavePhase("saved");
    else if (status.status === "FAILED") setSavePhase(null);
    else if (status.status === "ACCEPTED") setSavePhase(status.phase === "verifying" ? "verifying" : null);
  };
  return { savePhase, onStatus };
}

/** The Client onboarding tab shows every failure inline (with the rejected field), so there is no error toast. */
export function useInitializeTenantOnboarding() {
  const { t } = useTranslation();
  const { savePhase, onStatus } = useOnboardingSavePhase();
  const mutation = useAmberMutation({
    mutationFn: initializeTenantOnboarding,
    pollBasePath: TENANT_ONBOARDING_POLL_BASE_PATH,
    successMessage: t("iam.onboarding.toast.initialized", "Client onboarding initialized"),
    invalidateKeys: [tenantOnboardingKeys.all, brandBrainKeys.all, auditLogKeys.all],
    skipErrorToast: SHOWN_INLINE,
    onStatus,
  });
  return { ...mutation, savePhase };
}

export function useEditTenantOnboarding() {
  const { t } = useTranslation();
  const { savePhase, onStatus } = useOnboardingSavePhase();
  const mutation = useAmberMutation({
    mutationFn: editTenantOnboarding,
    pollBasePath: TENANT_ONBOARDING_POLL_BASE_PATH,
    successMessage: t("iam.onboarding.toast.updated", "Client onboarding updated"),
    invalidateKeys: [tenantOnboardingKeys.all, brandBrainKeys.all, auditLogKeys.all],
    skipErrorToast: SHOWN_INLINE,
    onStatus,
  });
  return { ...mutation, savePhase };
}

export function useAdminUpdateTenantSettings() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, data }: {
      tenantId: string;
      data: {
        defaultIndustry?: string | null;
        defaultBrand?: string | null;
        customerPartition?: string | null;
        conversionsMonthly?: number;
        conversionsWeekly?: number;
        currency?: CurrencyCode;
        defaultNorthStar?: NorthStarKpi | null;
        marketplaceOrdersInboxEmails?: string[];
        marketplaceOrderHandoffDefaults?: MarketplaceOrderHandoffDefaults;
        industryDefaultBrands?: IndustryDefaultBrandUpdate[];
        bqSyncFailureSlackAlertEnabled?: boolean;
        enabledOpportunityTypes?: EnabledOpportunityTypes;
      };
      /** Suppress the success toast — set when a sibling mutation (e.g. the tenant update) already toasts for the same Save. Defaults to shown. */
      showToast?: boolean;
    }) =>
      adminUpdateTenantSettings(tenantId, data),
    onSuccess: (_result, variables) => {
      if (variables.showToast !== false) toast.success(t("iam.toast.organizationUpdated"));
      void qc.invalidateQueries({ queryKey: identityHubKeys.tenantsPrefix() });
      void qc.invalidateQueries({ queryKey: identityHubKeys.tenantSettings(variables.tenantId) });
      void qc.invalidateQueries({ queryKey: tenantSettingsKeys.settings(variables.tenantId) });
      void qc.invalidateQueries({ queryKey: tenantSettingsKeys.competitors(variables.tenantId) });
      // Reset, not invalidate: onboarding and Brand Brain must not stay editable on the previous customer's data.
      if (variables.data.customerPartition !== undefined) {
        void qc.resetQueries({ queryKey: tenantOnboardingKeys.tenant(variables.tenantId) });
        void qc.resetQueries({ queryKey: brandBrainKeys.tenant(variables.tenantId) });
      }
      void qc.invalidateQueries({
        queryKey: personaOpportunitySetKeys.ownBrand(
          normalizedPersonaOpportunitySetTenantId(variables.tenantId),
        ),
      });
      invalidateIndustriesForTenant(qc, variables.tenantId);
      if ('conversionsMonthly' in variables.data || 'conversionsWeekly' in variables.data || 'currency' in variables.data) {
        void qc.invalidateQueries({ queryKey: dashboardKeys.all });
      }
    },
    onError: (_error, variables) => {
      // The API refuses a default brand whose industry roster is not cached; refetching
      // the rosters re-warms that cache so the next Save can be validated.
      for (const { industry } of variables.data.industryDefaultBrands ?? []) {
        void qc.invalidateQueries({ queryKey: identityHubKeys.industryBrands(industry) });
      }
    },
  });
}

export function useReplaceTenantPreflightMarkets() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, refs }: {
      tenantId: string;
      refs: AddPreflightMarketInput[];
      /** Suppress the success toast when a sibling mutation already toasts for the same Save. */
      showToast?: boolean;
    }) => replaceAdminTenantPreflightMarketRefs(tenantId, refs),
    onSuccess: (refs, variables) => {
      if (variables.showToast !== false) toast.success(t("iam.toast.organizationUpdated"));
      qc.setQueryData(identityHubKeys.tenantPreflightMarketRefs(variables.tenantId), refs);
    },
  });
}

export function useTriggerTenantSync() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => triggerTenantSync(tenantId),
    onSuccess: (_result, tenantId) => {
      toast.success(t("iam.toast.tenantSyncTriggered", "Sync triggered — products and industries will update shortly"));
      void qc.invalidateQueries({ queryKey: identityHubKeys.tenantSyncRuns(tenantId) });
    },
  });
}

// ─── Access Group Mutations ─────────────────────────────────────────

export function useCreateAccessGroup() {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: createAccessGroup,
    pollBasePath: "/access",
    successMessage: t("iam.toast.accessGroupCreated"),
    invalidateKeys: userInvalidateKeys,
  });
}

export function useCreateAccessGroupsBulk() {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: createAccessGroupsBulk,
    pollBasePath: "/access",
    successMessage: (vars) =>
      t("iam.toast.accessGroupsCreated", "Created role in {{count}} organizations", { count: vars.tenantIds.length }),
    invalidateKeys: userInvalidateKeys,
  });
}

export function useUpdateAccessGroup() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useAmberMutation({
    mutationFn: updateAccessGroup,
    pollBasePath: "/access",
    successMessage: t("iam.toast.accessGroupUpdated"),
    onSuccess: (_result, variables) => {
      const { accessId, tenantId, changes } = variables;
      const updatedAt = new Date().toISOString();
      patchAccessByTenantCaches(qc, tenantId, (old) =>
        old?.map((g) => (g.id === accessId ? { ...g, ...changes, updatedAt } : g)),
      );
    },
  });
}

export function useUpdateAccessPermissions() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useAmberMutation({
    mutationFn: updateAccessPermissions,
    pollBasePath: "/access",
    successMessage: t("iam.toast.permissionsUpdated"),
    onSuccess: (_result, variables) => {
      patchAccessPermissionsInCache(qc, {
        accessId: variables.accessId,
        tenantId: variables.tenantId,
        permissions: variables.permissions,
      });
    },
    beforeToast: async (_result, variables) => {
      const auth = useAuth.getState();
      if (auth.user?.acs.includes(variables.accessId)) {
        await refreshNexusToken().catch(() => undefined);
      }
    },
  });
}

/** Refreshed before the success toast: the lists a catalog-role save changes on screen. */
const templateInvalidateKeys = [identityHubKeys.templatesPrefix(), auditLogKeys.all] as const;

/**
 * Role saves and org provisioning touch every IAM query. Refetching all of them can take
 * seconds for a super tenant, so it runs after the toast instead of holding the dialog open.
 */
function refreshIdentityHubInBackground(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: identityHubKeys.all });
}

export function useAccessTemplates(filters: AccessTemplateListFilters = {}, opts: { enabled?: boolean } = {}) {
  return useQuery({
    ...accessTemplatesQueryOptions(filters),
    enabled: opts.enabled ?? true,
  });
}

export function useAccessTemplate(templateId: string, opts: { enabled?: boolean } = {}) {
  return useQuery({
    ...accessTemplateQueryOptions(templateId),
    enabled: (opts.enabled ?? true) && !!templateId,
  });
}

const TEMPLATE_SAVE_ERROR = "Something went wrong. Please try again.";

function useTemplateSaveErrorMessage() {
  const { t } = useTranslation();
  const fallback = t("common.error.generic", TEMPLATE_SAVE_ERROR);
  return (err: unknown) => resolveTemplateSaveErrorMessage(err, t, fallback);
}

export function useCreateAccessTemplate() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const resolveErrorMessage = useTemplateSaveErrorMessage();
  return useAmberMutation({
    mutationFn: createAccessTemplate,
    pollBasePath: "/templates",
    successMessage: t("iam.toast.templateCreated", "Role created"),
    invalidateKeys: templateInvalidateKeys,
    onSuccess: () => refreshIdentityHubInBackground(qc),
    resolveErrorMessage,
  });
}

export function useUpdateAccessTemplate() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const resolveErrorMessage = useTemplateSaveErrorMessage();
  return useAmberMutation({
    mutationFn: updateAccessTemplate,
    pollBasePath: "/templates",
    successMessage: t("iam.toast.templateUpdated", "Role updated"),
    invalidateKeys: templateInvalidateKeys,
    onSuccess: () => refreshIdentityHubInBackground(qc),
    resolveErrorMessage,
  });
}

export function usePreviewAccessTemplateCascade() {
  const resolveErrorMessage = useTemplateSaveErrorMessage();
  return useMutation({
    mutationFn: previewAccessTemplateCascade,
    onError: (err) => {
      Sentry.captureException(err);
      const message = resolveErrorMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

export function useChangeAccessTemplateStatus() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useAmberMutation({
    mutationFn: changeAccessTemplateStatus,
    pollBasePath: "/templates",
    successMessage: (vars) =>
      vars.status === "ARCHIVED"
        ? t("iam.toast.templateArchived")
        : t("iam.toast.templateRestored"),
    invalidateKeys: templateInvalidateKeys,
    onSuccess: () => refreshIdentityHubInBackground(qc),
  });
}

const templateMembershipInvalidateKeys = [
  identityHubKeys.templatesPrefix(),
  identityHubKeys.users(),
  identityHubKeys.all,
  auditLogKeys.all,
] as const;

const WILDCARD_NAME_CONFLICT = "WILDCARD_NAME_CONFLICT";
const WILDCARD_NAME_CONFLICT_MESSAGE =
  "A different role already uses this name on the organization.";
const GRANT_TEMPLATE_SERIALIZATION_CONFLICT = "GRANT_TEMPLATE_SERIALIZATION_CONFLICT";
const GRANT_TEMPLATE_SERIALIZATION_MESSAGE =
  "Could not assign this role because another update was in progress. Please try again.";

/**
 * Known grant codes map to fixed copy. Every other worker failure stays on the
 * default leak gate so raw Prisma text cannot reach the toast.
 */
function useGrantAccessTemplateErrorMessage() {
  const { t } = useTranslation();
  const conflict = t("iam.toast.wildcardNameConflict", WILDCARD_NAME_CONFLICT_MESSAGE);
  const serialization = t("iam.toast.grantSerializationConflict", GRANT_TEMPLATE_SERIALIZATION_MESSAGE);
  return (err: unknown): string | null => {
    if (!(err instanceof AmberWorkerFailureError)) {
      return resolveAmberErrorToastMessage(err);
    }
    if (err.code === WILDCARD_NAME_CONFLICT) return conflict;
    if (err.code === GRANT_TEMPLATE_SERIALIZATION_CONFLICT) return serialization;
    return resolveAmberErrorToastMessage(err);
  };
}

export function useGrantAccessTemplate() {
  const { t } = useTranslation();
  const resolveErrorMessage = useGrantAccessTemplateErrorMessage();
  return useAmberMutation({
    mutationFn: grantAccessTemplate,
    pollBasePath: "/templates",
    successMessage: t("iam.toast.fixedRoleGranted", "Role assigned"),
    invalidateKeys: templateMembershipInvalidateKeys,
    resolveErrorMessage,
  });
}

export function useRevokeAccessTemplate(opts?: { beforeToast?: () => Promise<void> }) {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: revokeAccessTemplate,
    pollBasePath: "/templates",
    successMessage: t("iam.toast.fixedRoleRevoked", "Fixed role removed"),
    invalidateKeys: templateMembershipInvalidateKeys,
    beforeToast: opts?.beforeToast,
  });
}

/**
 * Fixed memberships are deleted too, so template member lists refresh as well.
 * Skips count against the whole selection: the dialog sends only eligible ids,
 * so users it excluded never reach the server's skip counts.
 */
export function useRevokeAllUserAccess(opts?: { beforeToast?: () => Promise<void> }) {
  const { t } = useTranslation();
  return useAmberMutation({
    mutationFn: revokeAllUserAccess,
    pollBasePath: "/users",
    successMessage: (variables, result) => {
      const removedUsers = typeof result.removedUsers === "number" ? result.removedUsers : 0;
      return t("iam.toast.allRolesRemovedCount", {
        count: removedUsers,
        skipped: Math.max(0, variables.selectedCount - removedUsers),
        defaultValue: "Roles removed from {{count}} users · {{skipped}} skipped",
      });
    },
    invalidateKeys: templateMembershipInvalidateKeys,
    beforeToast: opts?.beforeToast,
  });
}

const ROLE_IN_USE = "ROLE_IN_USE";
const ROLE_IN_USE_MESSAGE =
  "People still have this role in this organization or a pending invite for it. Replace their role first.";
const COLD_ASSIGN_REQUIRES_ORGANIZATION = "COLD_ASSIGN_REQUIRES_ORGANIZATION";
const COLD_ASSIGN_ORG_MESSAGE =
  "Only an active organization can have a Wildcard role added or removed.";
const COLD_ASSIGN_REQUIRES_ACTIVE_WILDCARD = "COLD_ASSIGN_REQUIRES_ACTIVE_WILDCARD";
const COLD_ASSIGN_WILDCARD_MESSAGE =
  "Only an active Wildcard role can be added to or removed from an organization.";
const WILDCARD_RESTORE_IN_USE = "WILDCARD_RESTORE_IN_USE";
const WILDCARD_RESTORE_IN_USE_MESSAGE =
  "A deleted role with this name still has members or a pending invite. Their access was left unchanged.";

const orgCatalogRoleInvalidateKeys = [
  identityHubKeys.accessByTenantsPrefix(),
  identityHubKeys.all,
  identityHubKeys.templatesPrefix(),
  auditLogKeys.all,
] as const;

function useOrgCatalogRoleErrorMessage() {
  const { t } = useTranslation();
  const conflict = t("iam.toast.wildcardNameConflict", WILDCARD_NAME_CONFLICT_MESSAGE);
  const inUse = t("iam.toast.roleInUse", ROLE_IN_USE_MESSAGE);
  const organization = t("iam.toast.coldAssignRequiresOrganization", COLD_ASSIGN_ORG_MESSAGE);
  const wildcard = t("iam.toast.coldAssignRequiresActiveWildcard", COLD_ASSIGN_WILDCARD_MESSAGE);
  const restoreInUse = t("iam.toast.wildcardRestoreInUse", WILDCARD_RESTORE_IN_USE_MESSAGE);
  return (err: unknown): string | null => {
    if (!(err instanceof AmberWorkerFailureError)) {
      return resolveAmberErrorToastMessage(err);
    }
    if (err.code === WILDCARD_NAME_CONFLICT) return conflict;
    if (err.code === WILDCARD_RESTORE_IN_USE) return restoreInUse;
    if (err.code === ROLE_IN_USE) return inUse;
    if (err.code === COLD_ASSIGN_REQUIRES_ORGANIZATION) return organization;
    if (err.code === COLD_ASSIGN_REQUIRES_ACTIVE_WILDCARD) return wildcard;
    return resolveAmberErrorToastMessage(err);
  };
}

export function useApplyTemplateToOrg() {
  const { t } = useTranslation();
  const resolveErrorMessage = useOrgCatalogRoleErrorMessage();
  return useAmberMutation({
    mutationFn: applyTemplateToOrg,
    pollBasePath: "/templates",
    successMessage: t("iam.toast.wildcardRoleAdded", "Role added to the organization"),
    invalidateKeys: orgCatalogRoleInvalidateKeys,
    resolveErrorMessage,
  });
}

export function useRemoveTemplateFromOrg() {
  const { t } = useTranslation();
  const resolveErrorMessage = useOrgCatalogRoleErrorMessage();
  return useAmberMutation({
    mutationFn: removeTemplateFromOrg,
    pollBasePath: "/templates",
    successMessage: t("iam.toast.wildcardRoleRemoved", "Role removed from the organization"),
    invalidateKeys: orgCatalogRoleInvalidateKeys,
    resolveErrorMessage,
  });
}

export function useDeleteAccessGroup() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useAmberMutation({
    mutationFn: deleteAccessGroup,
    pollBasePath: "/access",
    successMessage: t("iam.toast.accessGroupDeleted"),
    invalidateKeys: userInvalidateKeys,
    onSuccess: (_result, variables) => {
      const { accessId, tenantId } = variables as { accessId: string; tenantId: string };
      // Optimistic UI update for the active list — the includeArchived list
      // refetches via `userInvalidateKeys` (`identityHubKeys.all`).
      qc.setQueryData<AccessGroupItem[]>(
        identityHubKeys.accessByTenant(tenantId),
        (old) => old?.filter((g) => g.id !== accessId),
      );
    },
  });
}

export function useRestoreAccessGroup() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useAmberMutation({
    mutationFn: restoreAccessGroup,
    pollBasePath: "/access",
    successMessage: t("iam.toast.accessGroupRestored", "Role restored"),
    onSuccess: (_result, variables) => {
      const { accessId, tenantId } = variables;
      void qc.invalidateQueries({ queryKey: identityHubKeys.access(accessId) });
      void qc.invalidateQueries({ queryKey: identityHubKeys.accessByTenant(tenantId) });
      void qc.invalidateQueries({
        queryKey: identityHubKeys.accessByTenant(tenantId, { includeArchived: true }),
      });
    },
  });
}

// ─── Invite ─────────────────────────────────────────────────────────

/**
 * True when an invite mutation failed because the invitee already accepted
 * the invite (a separate session redeemed it after the admin's list loaded).
 * The backend tags this case with a dedicated code so we can show a precise
 * message instead of the generic failure toast.
 */
function isInviteAlreadyAccepted(err: unknown): boolean {
  return err instanceof ApiError && err.code === "INVITE_ALREADY_ACCEPTED";
}

/**
 * True when a regenerate/copy-link failed because the email no longer has a
 * pending invite (it was accepted, expired, or revoked since the list loaded).
 */
function isInviteNoLongerPending(err: unknown): boolean {
  return err instanceof ApiError && err.code === "NO_PENDING_INVITE";
}

/** Which cache surfaces an invite mutation should refresh, beyond the invites list (always refreshed). */
interface InviteMutationSurfaces {
  users?: boolean;
  auditLog?: boolean;
}

function invalidateInviteSurfaces(qc: ReturnType<typeof useQueryClient>, surfaces: InviteMutationSurfaces = {}) {
  qc.invalidateQueries({ queryKey: identityHubKeys.invites() });
  if (surfaces.users) qc.invalidateQueries({ queryKey: identityHubKeys.users() });
  if (surfaces.auditLog) qc.invalidateQueries({ queryKey: auditLogKeys.all });
}

/**
 * Maps the shared invite-mutation error codes (already-accepted / no-longer-
 * pending — the invite may have changed state in another session since the
 * list loaded) to precise copy; falls back to the caller's message otherwise.
 */
function resolveInviteMutationError(err: unknown, t: ReturnType<typeof useTranslation>["t"], fallback: string): string {
  if (isInviteAlreadyAccepted(err)) {
    return t("iam.toast.inviteAlreadyAccepted", "This invite was already accepted — the list has been refreshed.");
  }
  if (isInviteNoLongerPending(err)) {
    return t("iam.toast.inviteNoLongerPending", "This invite is no longer pending — the list has been refreshed.");
  }
  return fallback;
}

interface InviteMutationConfig<TVars, TResult> {
  mutationFn: (vars: TVars) => Promise<TResult>;
  /** Toast + cache invalidation on success. Omit for read-only mutations (e.g. fetching a link to copy). */
  onSuccessConfig?: { message: string; invalidate?: InviteMutationSurfaces };
  /** Cache surfaces to refresh on failure — the invite may have changed state since the list loaded. */
  invalidateOnError?: InviteMutationSurfaces;
  fallbackErrorMessage: string;
}

/**
 * Shared shape for every invite mutation that isn't a bulk-create: Sentry-report
 * on failure, resolve the already-accepted/no-longer-pending error codes to
 * precise copy, and refresh whichever cache surfaces the caller specifies.
 */
function useInviteMutation<TVars, TResult>(config: InviteMutationConfig<TVars, TResult>) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: config.mutationFn,
    onSuccess: () => {
      if (!config.onSuccessConfig) return;
      invalidateInviteSurfaces(qc, config.onSuccessConfig.invalidate);
      toast.success(config.onSuccessConfig.message);
    },
    onError: (err) => {
      Sentry.captureException(err);
      invalidateInviteSurfaces(qc, config.invalidateOnError);
      if (shouldSuppressLocalApiErrorToast(err)) return;
      toast.error(resolveInviteMutationError(err, t, config.fallbackErrorMessage));
    },
    meta: { skipGlobalError: true },
  });
}

export function useCreateInvite() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createInvite,
    onSuccess: () => {
      invalidateInviteSurfaces(qc, { users: true, auditLog: true });
      toast.success(t("iam.toast.inviteCreated"));
    },
    onError: (err) => {
      Sentry.captureException(err);
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

export function useCreateInvitesBulk() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createInvitesBulk,
    onSuccess: (results: BulkInviteResultItem[], variables) => {
      invalidateInviteSurfaces(qc, { users: true, auditLog: true });
      if (variables.templateGrants && variables.templateGrants.length > 0) {
        void qc.invalidateQueries({ queryKey: identityHubKeys.accessByTenantsPrefix() });
      }
      const succeeded = results.filter((r) => r.success).length;
      const failedCount = results.filter((r) => !r.success).length;
      if (succeeded > 0) {
        toast.success(succeeded === 1 ? t("iam.toast.inviteCreated") : t("iam.toast.invitesCreated", { count: succeeded }));
      }
      if (failedCount > 0) {
        toast.error(t("iam.toast.invitesFailed", { count: failedCount }));
      }
    },
    onError: (err) => {
      Sentry.captureException(err);
      // The email-cap breach gets a specific message; everything else falls
      // back to the generic toast (raw backend text is never surfaced).
      if (err instanceof ApiError && err.code === INVITE_EMAILS_LIMIT_CODE && err.status !== 401 && err.status !== 403) {
        toast.error(t("iam.inviteDialog.emailsTooMany", { max: MAX_INVITE_EMAILS }));
        return;
      }
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

export function useRegenerateInvite() {
  const { t } = useTranslation();
  return useInviteMutation({
    mutationFn: regenerateInvite,
    onSuccessConfig: {
      message: t("iam.toast.inviteRegenerated", "Invite link regenerated"),
      invalidate: { users: true, auditLog: true },
    },
    invalidateOnError: { users: true },
    fallbackErrorMessage: t("iam.toast.inviteRegenerateFailed", "Failed to regenerate invite"),
  });
}

/**
 * Fetch an existing invite's link (token) for copying, WITHOUT rotating it —
 * unlike regenerate, this leaves the invitee's emailed link valid (AD-1714).
 * Read-only, so nothing is invalidated on success; the caller writes the link
 * to the clipboard. On error the invites list is refreshed (the invite may
 * have been accepted/expired/revoked since the list loaded).
 */
export function useInviteLink() {
  const { t } = useTranslation();
  return useInviteMutation({
    mutationFn: (id: string) => fetchInviteLink(id),
    invalidateOnError: { users: true },
    fallbackErrorMessage: t("iam.toast.inviteLinkFailed", "Couldn’t copy the invite link — the list has been refreshed."),
  });
}

// ─── Invites listing ────────────────────────────────────────────────

/** @deprecated Use useIamInvites for paginated Identity Hub invites tab */
export function useInvitesList(filters: InviteListFilters, opts: { enabled?: boolean } = {}) {
  return useQuery({ ...invitesListQueryOptions(filters), enabled: opts.enabled ?? true });
}

export function useIamInvites(filters: InviteListFilters, opts: { enabled?: boolean } = {}) {
  return useInfiniteQuery({ ...invitesInfiniteQueryOptions(filters), enabled: opts.enabled ?? true });
}

export function useResendInvite() {
  const { t } = useTranslation();
  return useInviteMutation({
    mutationFn: ({ id, ttlHours }: { id: string; ttlHours?: number }) => resendInvite(id, ttlHours),
    onSuccessConfig: {
      message: t("iam.toast.inviteResent", "Invite email resent"),
      invalidate: { auditLog: true },
    },
    fallbackErrorMessage: t("iam.toast.inviteResendFailed", "Failed to resend invite"),
  });
}

export function useRevokeInvite() {
  const { t } = useTranslation();
  return useInviteMutation({
    mutationFn: revokeInvite,
    onSuccessConfig: {
      message: t("iam.toast.inviteRevoked", "Invite revoked"),
      invalidate: { users: true, auditLog: true },
    },
    invalidateOnError: { users: true },
    fallbackErrorMessage: t("iam.toast.inviteRevokeFailed", "Failed to revoke invite"),
  });
}

/**
 * Delete an expired invite record. Shares the revoke endpoint (both hard-delete
 * the row) but is surfaced separately so an already-dead invite reads as
 * "remove this record" rather than "cancel a live link".
 */
export function useDeleteInvite() {
  const { t } = useTranslation();
  return useInviteMutation({
    mutationFn: revokeInvite,
    onSuccessConfig: {
      message: t("iam.toast.inviteDeleted", "Invite record deleted"),
      invalidate: { auditLog: true },
    },
    fallbackErrorMessage: t("iam.toast.inviteDeleteFailed", "Failed to delete invite record"),
  });
}

// ─── Access for Multiple Users ──────────────────────────────────────

/**
 * Loads access trees for every visible user in a single batched request
 * (chunked for very large sets) and exposes them as a Map keyed by userId.
 *
 * This replaced a per-user `useQuery` fan-out that fired one
 * `GET /users/:userId/access` per row — on a 300-person tenant that was 300
 * requests, enough to trip production ingress limits (surfacing as CORS
 * errors). The batch endpoint collapses that into one call.
 *
 * The Map is memoized on `query.data` so its reference only changes when the
 * fetched data changes — downstream consumers (dashboard derived state, KPI
 * insights, table rows) memo-depend on `accessByUser`, so a fresh Map every
 * render would cascade into recomputing every derivation on each re-render.
 */
export function useAllUsersAccess(userIds: string[]) {
  const chunks = useMemo(
    () => stableChunk(userIds, MAX_USERS_ACCESS_BATCH),
    [userIds],
  );
  const queries = useQueries({
    queries: chunks.map((ids) => usersAccessChunkQueryOptions(ids)),
  });
  const previousByUserRef = useRef(new Map<string, UserAccessEntry[]>());
  const slices = useMemo(
    () => queries.map((query) => ({
      data: query.data,
      isPlaceholderData: query.isPlaceholderData,
      isFetching: query.isFetching,
      isLoading: query.isLoading,
      isError: query.isError,
    })),
    [queries],
  );

  const accessByUser = useMemo(
    () => mergeUsersAccess(userIds, chunks, slices, previousByUserRef.current),
    [userIds, chunks, slices],
  );

  useEffect(() => {
    rememberSettledUsersAccess(chunks, slices, previousByUserRef.current);
  }, [chunks, slices]);

  const accessPhaseByUser = useMemo(() => {
    const phases = new Map<string, UserAccessPhase>();
    for (const userId of userIds) {
      phases.set(userId, userAccessPhase(userId, chunks, slices, accessByUser));
    }
    return phases;
  }, [userIds, chunks, slices, accessByUser]);

  const isLoading = queries.some((q) => q.isLoading && q.data === undefined);
  const isError = queries.length > 0 && queries.every((q) => q.isError);
  const refetch = useCallback(async () => {
    await Promise.all(queries.map((q) => q.refetch()));
  }, [queries]);

  return { accessByUser, accessPhaseByUser, isLoading, isError, refetch };
}

// ─── Access Groups for Multiple Tenants ─────────────────────────────

/**
 * Loads access groups for every visible tenant in a single batched request
 * (chunked for very large sets) and exposes them as a Map keyed by tenantId.
 *
 * This replaced a per-tenant `useQuery` fan-out that fired one
 * `GET /access/by-tenant/:tenantId` per tenant — the same flooding pattern the
 * People tab had. The Map is memoized on `query.data` so downstream memo
 * consumers stay referentially stable across unrelated re-renders.
 */
export function useAccessGroupsForTenants(
  tenantIds: string[],
  opts: { includeArchived?: boolean } = {},
) {
  const includeArchived = opts.includeArchived === true;
  const chunks = useMemo(
    () => stableChunk(tenantIds, MAX_ACCESS_BY_TENANT_BATCH),
    [tenantIds],
  );
  const queryList = useMemo(
    () => chunks.map((ids) => accessGroupsByTenantsChunkQueryOptions(ids, { includeArchived })),
    [chunks, includeArchived],
  );
  const queries = useQueries({ queries: queryList });

  // `useQueries` returns a new array every render. Key the map on mode,
  // query identity, and data timestamps so an archived read cannot reuse the
  // active map when timestamps and tenant ids happen to match.
  const dataVersion = accessGroupsMapVersion({
    includeArchived,
    queryKeys: queryList.map((query) => query.queryKey),
    chunks: queries.map((query) => ({
      dataUpdatedAt: query.dataUpdatedAt,
      tenantIds: query.data ? Object.keys(query.data) : [],
    })),
  });
  const groupsByTenant = useMemo(() => {
    const map = new Map<string, AccessGroupItem[]>();
    for (const query of queries) {
      if (!query.data) continue;
      for (const [tenantId, groups] of Object.entries(query.data)) {
        map.set(tenantId, groups);
      }
    }
    return map;
    // `queries` is a fresh array every render. `dataVersion` changes when mode, query key, or chunk data does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataVersion]);

  const isLoading = queries.some((q) => q.isLoading && q.data === undefined);
  // `isError` means every chunk failed. One failed chunk still leaves
  // `isLoading` false, so callers must not treat that partial map as finished.
  const isError = queries.length > 0 && queries.every((q) => q.isError);
  const hasFailedChunk = queries.some((q) => q.isError);
  const refetch = useCallback(async () => {
    await Promise.all(queries.map((q) => q.refetch()));
  }, [queries]);

  return { groupsByTenant, isLoading, isError, hasFailedChunk, refetch };
}

/**
 * Identity for the grouped access-group map. Mode and query key are part of
 * the version because `dataUpdatedAt` plus tenant ids can collide across an
 * active-only read and an include-archived read of the same orgs.
 */
export function accessGroupsMapVersion(args: {
  includeArchived: boolean;
  queryKeys: readonly unknown[];
  chunks: ReadonlyArray<{ dataUpdatedAt: number; tenantIds: readonly string[] }>;
}): string {
  return [
    args.includeArchived ? "archived" : "active",
    ...args.queryKeys.map((key) => JSON.stringify(key)),
    ...args.chunks.map(
      (chunk) => `${chunk.dataUpdatedAt}:${[...chunk.tenantIds].sort().join(",")}`,
    ),
  ].join("|");
}

// ─── Org Toggle Handler ─────────────────────────────────────────────

export interface OrgToggleOpts {
  onRoleRequired?: (tenant: DerivedTenant, groups: AccessGroupItem[]) => void;
}

export function useOrgToggleHandler(
  selectedUserId: string,
  selectedUserEmail: string | undefined,
  allAccessEntries: UserAccessEntry[],
  activeTenantCount: number,
  loggedInEmail: string | undefined,
  activeTenantId: string | null,
  opts?: OrgToggleOpts,
) {
  const { t } = useTranslation();
  const grantableKeys = useGrantablePermissionTupleKeys();
  // Single-grant path: await sidebar sync before the success toast so users can't act on a stale JWT.
  const grantAccess = useGrantAccess({
    beforeToast: () => syncSidebarIfActiveUser(selectedUserEmail),
  });
  // Multi-revoke path: sync is deferred to run once after all revokes settle (see handleOrgToggle).
  const revokeAccess = useRevokeAccess();

  const handleOrgToggle = useCallback(
    async (tenant: DerivedTenant, checked: boolean) => {
      if (!selectedUserId) return;

      if (!checked) {
        // Guard: prevent detaching the logged-in user's active tenant
        if (loggedInEmail && selectedUserEmail === loggedInEmail && tenant.id === activeTenantId) {
          toast.warning(t("iam.toast.cannotDetachActiveOrg"));
          return;
        }
        // Guard: prevent turning off the last active org
        if (activeTenantCount <= 1) {
          toast.warning(t("iam.toast.atLeastOneOrgRequired"));
          return;
        }
        // Revoke all access entries for this tenant in parallel, then refresh the JWT once —
        // avoids firing /auth/refresh once per revoke when a tenant has multiple access entries.
        const revocable = tenant.accessEntries.filter((entry) => isPerTenantAssignable(entry.access));
        if (revocable.length === 0) return;
        const results = await Promise.allSettled(
          revocable.map((entry) =>
            revokeAccess.mutateAsync({ userId: selectedUserId, accessId: entry.accessId, tenantId: tenant.id }),
          ),
        );
        if (results.some((r) => r.status === "fulfilled")) {
          await syncSidebarIfActiveUser(selectedUserEmail);
        }
      } else {
        try {
          const groups = grantablePerTenantGroups(
            await fetchAccessByTenant(tenant.id),
            grantableKeys,
          );
          if (groups.length === 0) {
            toast.warning(t("iam.toast.noAccessGroupsAvailable"));
            return;
          }
          // Multiple roles → delegate to caller for picker; single role → auto-grant
          if (groups.length > 1 && opts?.onRoleRequired) {
            opts.onRoleRequired(tenant, groups);
            return;
          }
          grantAccess.mutate({ userId: selectedUserId, accessId: groups[0].id, tenantId: tenant.id });
        } catch (err) {
          Sentry.captureException(err);
          toast.error(t("iam.toast.fetchAccessGroupsFailed"));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedUserId, activeTenantCount, loggedInEmail, selectedUserEmail, activeTenantId, grantAccess, revokeAccess, grantableKeys, t, opts?.onRoleRequired],
  );

  // Confirms a role selection after onRoleRequired fires
  const grantForRole = useCallback(
    (tenantId: string, accessId: string) => {
      if (!selectedUserId) return;
      grantAccess.mutate({ userId: selectedUserId, accessId, tenantId });
    },
    [selectedUserId, grantAccess],
  );

  const isPending = grantAccess.isPending || revokeAccess.isPending;

  // The tenant ID that should be locked (non-detachable) when editing self
  const lockedTenantId =
    loggedInEmail && selectedUserEmail === loggedInEmail ? activeTenantId : null;

  return { handleOrgToggle, isPending, lockedTenantId, grantForRole };
}
